import { Injectable, OnDestroy } from '@angular/core';
import { StorageApiService } from '@shared/services/storage-api.service';

@Injectable({
  providedIn: 'root',
})
export class TabControllerService implements OnDestroy {
  private tabs: { [id: string]: { url: string; reference: Window | null } } =
    {};
  private readonly beforeUnloadEventListener: (
    event: BeforeUnloadEvent
  ) => void;

  constructor(private readonly storageApiService: StorageApiService) {
    const storedTabs = this.storageApiService.getLocalStorageData('openTabs');
    if (storedTabs) {
      const parsedTabs = JSON.parse(storedTabs);
      this.tabs = Object.keys(parsedTabs).reduce((acc, id) => {
        acc[id] = { url: parsedTabs[id], reference: null };
        return acc;
      }, {} as { [id: string]: { url: string; reference: Window | null } });
    }

    this.beforeUnloadEventListener = (event: BeforeUnloadEvent) => {
      this.cleanupTabs();
    };
    window.addEventListener('beforeunload', this.beforeUnloadEventListener);
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadEventListener);
  }

  public openTab(id: string, url: string): void {
    if (!this.isTabOpen(id)) {
      const tab = window.open(url, '_blank', 'noopener,noreferrer');
      if (tab) {
        this.tabs[id] = { url, reference: tab };
        this.saveTabs();
      }
    } else {
      this.tabs[id]?.reference?.focus();
    }
  }

  public closeTab(id: string): void {
    if (this.tabs[id]) {
      this.tabs[id].reference?.close();
      delete this.tabs[id];
      this.saveTabs();
    }
  }

  public isTabOpen(id: string): boolean {
    const tab = this.tabs[id]?.reference;
    return !!tab && !tab.closed;
  }

  public getGeneratedTabs(): { id: string; url: string }[] {
    return Object.keys(this.tabs).map((id) => ({
      id,
      url: this.tabs[id].url,
    }));
  }

  public closeAllTabs(): void {
    Object.keys(this.tabs).forEach((id) => {
      this.tabs[id].reference?.close();
    });
    this.tabs = {};
    this.saveTabs();
  }

  public cleanupTabs(): void {
    this.closeAllTabs();
    this.storageApiService.deleteLocalStorageData('generatedTabsOpen');
  }

  private saveTabs(): void {
    const serializedTabs = Object.keys(this.tabs).reduce((acc, id) => {
      acc[id] = this.tabs[id].url;
      return acc;
    }, {} as { [id: string]: string });
    this.storageApiService.setLocalStorageData(
      'generatedTabsOpen',
      serializedTabs
    );
  }
}
